import Button from '@/components/base/Button';
import Reveal from '@/components/base/Reveal';
import PageHero from '@/components/feature/PageHero';

const BENEFITS = [
  {
    icon: 'ri-focus-3-line',
    title: 'Relevant Introductions',
    body: 'Enquiries are matched to suppliers whose expertise genuinely suits the request.',
  },
  {
    icon: 'ri-wallet-3-line',
    title: 'No Membership Fees',
    body: 'There are no joining or subscription charges. Commission on qualifying bookings is agreed beforehand.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Personal Relationships',
    body: 'Daniella takes the time to learn about your business and what makes you a good match.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Opportunities to Grow',
    body: 'Build lasting relationships with customers who value being connected to the right people.',
  },
];

const STEPS = [
  { number: '01', title: 'Apply with your business details', body: 'Tell us who you are and what you offer.' },
  { number: '02', title: 'Daniella reviews your application', body: 'She may arrange a conversation to find out more.' },
  { number: '03', title: 'Agree referral terms', body: 'Commission and partnership terms are agreed before any introductions.' },
  { number: '04', title: 'You handle the delivery', body: 'Suppliers manage quotes, contracts, bookings and delivery directly.' },
];

export default function ForSuppliers() {
  return (
    <>
      <PageHero
        eyebrow="For Suppliers"
        title="Let&rsquo;s Make Great Connections"
        intro="Join the KOR38 Supplier Network — a carefully selected group of event professionals across Greater London."
        imageSrc="https://storage.helloreaddy.io/project_files/70bd3036-f013-4766-9098-d293cc2afad4/9e8686fa-dd3d-4ff2-a444-fcb3b6a9440d_compressed_Elegant-Gala-Canap-Preparation-v2.webp"
        imageAlt="Chefs preparing elegant canapes at a luxury gala event"
        roomyBottom
      />

      <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <p className="text-base md:text-lg leading-relaxed text-foreground-700">
            Are you an event professional looking to connect with more potential customers? KOR38 is a
            London-based personal sourcing and introduction service connecting people planning events
            with talented suppliers. We&rsquo;re building a carefully selected network across Greater
            London. Joining is completely free.
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-background-50">
        <div className="max-w-content mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">Why join</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {BENEFITS.map((benefit, index) => (
              <Reveal
                key={benefit.title}
                delay={index * 70}
                className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7"
              >
                <span className="w-12 h-12 rounded-md bg-secondary-900 flex items-center justify-center">
                  <i className={`${benefit.icon} text-2xl text-primary-400`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-5 font-heading text-lg md:text-xl text-foreground-950">
                  {benefit.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-foreground-700">{benefit.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-background-50">
        <div className="max-w-content mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">How it works</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((step, index) => (
              <Reveal
                key={step.number}
                delay={index * 70}
                className="rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <span className="font-heading text-3xl text-accent-500">{step.number}</span>
                <h3 className="mt-4 font-heading text-base md:text-lg text-foreground-950 leading-snug">
                  {step.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-foreground-700">{step.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 bg-background-50">
        <div className="max-w-content mx-auto">
          <div className="rounded-lg bg-[#1B1B1B] border border-white/10 p-6 md:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <p className="text-base leading-relaxed text-foreground-600 max-w-2xl">
              Applications are reviewed individually and there are no guaranteed leads or bookings.
              Applying does not mean automatic approval, listing in a public directory, or automatic
              agreement to supplier terms.
            </p>
            <div className="shrink-0">
              <Button to="/apply" variant="primary" size="lg">
                Apply to Join KOR38
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}